import React, { useState, useRef, useEffect } from 'react';
import { Globe, MoreVertical, CirclePlus, ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import { WebsiteData } from "../../types/CourseData";

interface WebsiteSidebarProps {
  websites: WebsiteData[];
  setWebsites: React.Dispatch<React.SetStateAction<WebsiteData[]>>;
  selectedWebsite: number;
  setSelectedWebsite: React.Dispatch<React.SetStateAction<number>>;
  systemConfig: { Id: string; ConfigValue: string; }[];
  addNewWebsites: () => void;
  triggerAutoSave: () => void;
  setCustomModal: React.Dispatch<React.SetStateAction<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "loading" | "confirmation" | "warning";
    onConfirm?: () => void;
  }>>;
}

interface ContextMenu {
  visible: boolean;
  x: number;
  y: number;
  courseId: number;
}

const WebsiteSidebar: React.FC<WebsiteSidebarProps> = ({
  websites,
  setWebsites,
  selectedWebsite,
  setSelectedWebsite,
  systemConfig,
  addNewWebsites,
  triggerAutoSave,
  setCustomModal
}) => {
  const [contextMenu, setContextMenu] = useState<ContextMenu>({
    visible: false,
    x: 0,
    y: 0,
    courseId: 0
  });

  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  const sortedCourses = [...websites].sort((a, b) => a.order - b.order);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>, courseId: number) => {
    const updatedWebsiteName = event.target.value;
    setWebsites(prevWebsites => 
      prevWebsites.map(website =>
        website.id === courseId 
          ? { ...website, website_name: updatedWebsiteName } 
          : website
      )
    );
  };

  const handleContextMenu = (e: React.MouseEvent, courseId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      courseId
    });
  };

  const closeContextMenu = () => {
    setContextMenu(prev => ({ ...prev, visible: false }));
  };

  const moveCourse = (courseId: number, direction: 'up' | 'down') => {
    setWebsites((prevCourses) => {
      const sortedCourses = [...prevCourses].sort((a, b) => a.order - b.order);
      const currentIndex = sortedCourses.findIndex((course) => course.id === courseId);
      
      if (
        (direction === 'up' && currentIndex === 0) ||
        (direction === 'down' && currentIndex === sortedCourses.length - 1)
      ) {
        return prevCourses;
      }
      
      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

      const updatedCourses = sortedCourses.map((course, index) => {
        if (index === currentIndex) {
          return { ...course, order: sortedCourses[newIndex].order };
        }
        if (index === newIndex) {
          return { ...course, order: sortedCourses[currentIndex].order };
        }
        return course;
      });
      return updatedCourses;
    });
    closeContextMenu();
  };

  const deleteCourse = (courseId: number) => {
    const warningMessage = 'Are you sure you want to delete website? This action cannot be undone.';
    
    setCustomModal({
      isOpen: true,
      title: "Delete Confirmation",
      message: warningMessage,
      type: "confirmation",
      onConfirm: async () => {
        setCustomModal(prev => ({ ...prev, isOpen: false }));
        
        setCustomModal({
          isOpen: true,
          title: "Deleting...",
          message: warningMessage,
          type: "loading",
        });

        try {
          const folderToDelete = websites.find(c => c.id === courseId);
          const isConnected = folderToDelete?.connectionState === 'connected';
          
          setWebsites(prev => {
            const filtered = prev.filter(c => c.id !== courseId);
            return filtered.map((course, index) => ({
              ...course,
              order: index + 1
            }));
          });

          // Trigger autosave after website deletion
          triggerAutoSave();
          
          if (selectedWebsite === courseId) {
            const remainingCourses = websites.filter(c => c.id !== courseId);
            setSelectedWebsite(remainingCourses[0]?.id || 0);
          }

          setCustomModal({
            isOpen: true,
            title: "Success!",
            message: 'Website deleted successfully',
            type: "success",
          });
        } catch (error) {
          console.log("Delete failed", error);
          setCustomModal({
            isOpen: true,
            title: "Error",
            message: 'Failed to delete website',
            type: "error",
          });
        }
      }
    });
    closeContextMenu();
  };

  const handleDragStart = (e: React.DragEvent, position: number) => {
    e.stopPropagation();
    dragItem.current = position;
    e.currentTarget.classList.add('opacity-50');
  };

  const handleDragEnter = (e: React.DragEvent, position: number) => {
    e.preventDefault();
    e.stopPropagation();
    dragOverItem.current = position;
    e.currentTarget.classList.add('bg-gray-50');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.currentTarget.classList.remove('bg-gray-50');
  };

  const handleDragEnd = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    e.currentTarget.classList.remove('opacity-50');

    if (dragItem.current !== null && dragOverItem.current !== null) {
      const copyListItems = [...websites];
      const dragItemContent = copyListItems[dragItem.current];
      
      copyListItems.splice(dragItem.current, 1);
      copyListItems.splice(dragOverItem.current, 0, dragItemContent);
      
      const reorderedItems = copyListItems.map((item, index) => ({
        ...item,
        order: index + 1,
      }));
      setWebsites(reorderedItems);
    }
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenu(prev => ({ ...prev, visible: false }));
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const maxAllowedWebsites = Number(systemConfig.find(item => item.Id === 'va_max_allowed_websites')?.ConfigValue) || 5;

  return (
    <div className="w-64 border-r border-gray-200 overflow-y-auto">
      {sortedCourses.map((course, index) => (
        <div
          key={course.id}
          draggable
          onDragStart={(e) => handleDragStart(e, index)}
          onDragEnter={(e) => handleDragEnter(e, index)}
          onDragLeave={handleDragLeave}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          className={`flex items-center justify-between p-4 cursor-move hover:bg-gray-50 
            ${selectedWebsite === course.id ? 'bg-gray-100' : ''}
            transition-opacity duration-200`}
        >
          <div 
            className="flex items-center space-x-3"
            onClick={() => setSelectedWebsite(course.id)}
          >
            <Globe className="h-5 w-5 text-gray-600" />
            <span className="text-sm" title={course?.website_name}>
              <input 
                type="text" 
                value={course.website_name ? course.website_name : `Website ${course.order}`}  
                onChange={(e) => handleNameChange(e, course.id)}  
                className="text-sm bg-transparent border-none outline-none w-full truncate cursor-pointer"
                readOnly
              />
            </span>
          </div>
          <button
            className="hover:bg-gray-100 p-1 rounded"
            onClick={(e) => {
              e.stopPropagation();
              handleContextMenu(e, course.id);
            }}
          >
            <MoreVertical className="h-4 w-4 text-gray-400" />
          </button>
        </div>
      ))}

      {/* Add new website button */}
      {websites.length < maxAllowedWebsites && (
        <div className="p-4">
          <button
            onClick={addNewWebsites}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
          >
            <CirclePlus className="h-6 w-6 text-gray-500" />
          </button>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          ref={contextMenuRef}
          className="fixed bg-white shadow-lg rounded-lg py-1 z-50"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-2"
            onClick={() => moveCourse(contextMenu.courseId, 'up')}
          >
            <ChevronUp className="h-4 w-4" />
            <span>Move Up</span>
          </button>
          <button
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-2"
            onClick={() => moveCourse(contextMenu.courseId, 'down')}
          >
            <ChevronDown className="h-4 w-4" />
            <span>Move Down</span>
          </button>
          <button
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-2 text-red-600"
            onClick={() => deleteCourse(contextMenu.courseId)}
          >
            <Trash2 className="h-4 w-4" />
            <span>Delete</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default WebsiteSidebar;